/**
 * TextareaField - Multi-line text input field component
 *
 * Task: T-P4-D2-F4b-4
 * Feature: P4-D2-F4 Web App Forms UI
 *
 * Textarea with character counter. Counter changes color near limit.
 * Grows with the answer (Gabriel 2026-09-02): the box is never a fixed
 * scrolling window, it is as tall as the text plus the minimum height.
 */

import { useLayoutEffect, useRef, type ChangeEvent } from 'react';
import type { TextFieldProps } from './types';
import { FieldWrapper } from './FieldWrapper';
import styles from './fields.module.css';

/** Size a textarea to its content. Height resets first so shrinking works too. */
export function fitTextareaToContent(textarea: HTMLTextAreaElement | null): void {
  if (!textarea) return;
  textarea.style.height = 'auto';
  textarea.style.height = `${textarea.scrollHeight}px`;
}

/**
 * TextareaField renders a multi-line text input with character counter.
 */
export function TextareaField({
  field,
  value,
  onChange,
  onBlur,
  error,
  style,
  nonInteractive,
}: TextFieldProps) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => { fitTextareaToContent(ref.current); }, [value]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };

  return (
    <FieldWrapper
      fieldId={field.fieldId}
      label={field.label}
      required={field.required}
      helpText={field.helpText}
      error={error}
      maxLength={field.maxLength}
      currentLength={value.length}
      style={style}
    >
      <textarea
        ref={ref}
        id={field.fieldId}
        name={field.fieldId}
        className={`${styles.input} ${styles.textarea} ${
          error ? styles.hasError : ''
        }`}
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={field.placeholder}
        maxLength={field.maxLength}
        minLength={field.minLength}
        readOnly={nonInteractive}
        aria-invalid={!!error}
        aria-describedby={error ? `${field.fieldId}-error` : undefined}
      />
    </FieldWrapper>
  );
}
